import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, User, Mail, Phone, MapPin, Heart, Shield, Banknote,
  Briefcase, Edit3, Archive, AlertTriangle, FileCheck2, FolderKanban,
  CalendarDays, Save, X, Send, Search, CheckCircle2, Check, Copy as CopyIcon,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Spinner, Field, Input, Select, Textarea, EmptyState,
} from "@/components/ui";
import { fmtDate } from "@/lib/dates";
import EmployeeReadinessCard from "@/features/compliance/EmployeeReadinessCard";
import AssignPersonalBookingModal from "@/features/schedule/AssignPersonalBookingModal";
import { isPresenting, MONEY_MASK } from "@/lib/presenting";

// =============================================================================
// EmployeeDetailScreen — /people/:employeeId  (T-126 phase 2)
//
// HR-side per-employee record. Read-only by default; manager+ can flip into
// edit mode for personal / employment / compliance fields. Archive button
// soft-deletes (is_active = false + end_date = today) without losing history.
//
// Sections shown:
//   - Header (name + status pills + role + edit/archive actions)
//   - Personal (contact, address, NoK, emergency medical)
//   - Employment (type, start/end, role, base rate, LAFHA, sub-company link)
//   - Compliance (TFN flag, super, bank, USI)
//   - Linked records summary (licences, leave balance, projects, policies)
//
// Linked-record summaries are read-only stat tiles with click-through to
// dedicated screens — keeps this page focused on the employee's "card" data.
// =============================================================================

type EmpType = "full_time" | "part_time" | "casual" | "subcontractor" | "labour_hire";

interface EmployeeRow {
  employee_id:    string;
  employee_guid:  string;
  first_name:     string;
  middle_name:    string | null;
  last_name:      string;
  preferred_name: string | null;
  date_of_birth:  string | null;
  email:          string | null;
  mobile:         string | null;
  home_phone:     string | null;
  address_line1:  string | null;
  address_line2:  string | null;
  suburb:         string | null;
  state:          string | null;
  postcode:       string | null;
  next_of_kin_name:         string | null;
  next_of_kin_relationship: string | null;
  next_of_kin_phone:        string | null;
  emergency_medical_notes:  string | null;
  blood_type:     string | null;
  employment_type:    EmpType;
  // Migration 328 — the person's ordinary working week under their award
  // (38 for full-timers, fewer for part-time). Leave accrual and "hours per
  // day of leave" are both computed from it: a day of leave = this / 5.
  ordinary_hours_per_week: number;
  primary_role_name:  string | null;
  job_title:          string | null;
  subcontractor_company_id: string | null;
  start_date:    string | null;
  end_date:      string | null;
  base_rate:     number | null;
  lafha_eligible: boolean;
  lafha_rate:    number | null;
  // T-5xx / migration 325 - can this person be put on the schedule? Until now
  // this was a hard-coded rule in this file (active, and not a subcontractor),
  // invisible and unchangeable. It is a column now, and this is where it is set.
  is_schedulable: boolean;
  usi:           string | null;
  tfn_on_file:   boolean;
  super_fund_name:     string | null;
  super_fund_usi:      string | null;
  super_member_number: string | null;
  bank_bsb:           string | null;
  bank_acct_number:   string | null;
  xero_employee_id:   string | null;
  profile_photo_url:  string | null;
  is_active:     boolean;
  created_at:    string;
  updated_at:    string;
  subcontractor_company: { legal_name: string; trading_name: string | null } | null;
}

// =============================================================================

export default function EmployeeDetailScreen() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const isDirector = hasRole(user, "director");

  const [editing, setEditing] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  // T-MFA — director resets a lost/replaced phone from here.
  const [mfaResetBusy, setMfaResetBusy] = useState(false);
  async function resetMfa() {
    if (!window.confirm(
      "Reset their MFA?\n\nThis wipes the authenticator on their old phone. " +
      "Next time they log in they'll be walked through setting up a new one. " +
      "Their password is unchanged.")) return;
    setMfaResetBusy(true);
    try {
      const res = await apiFetch("/admin/mfa-reset", {
        method: "POST",
        body: JSON.stringify({ employee_id: employeeId }),
      });
      const out = await res.json() as { detail?: string; error?: string };
      window.alert(res.ok
        ? (out.detail ?? "MFA reset.")
        : `Could not reset MFA: ${out.detail ?? out.error ?? res.statusText}`);
    } catch (e: any) {
      window.alert(`Could not reset MFA: ${e?.message ?? e}`);
    } finally {
      setMfaResetBusy(false);
    }
  }
  // T-249 — assign ad-hoc task modal
  const [assignTaskOpen, setAssignTaskOpen] = useState(false);

  const { data: emp, isLoading, error, refetch } = useQuery({
    queryKey: ["employee_detail", employeeId],
    enabled: !!employeeId,
    queryFn: async () => {
      // T-446 — sensitive fields moved to employee_private (director / payroll / self
      // only, via RLS). Fetch both and merge; for a manager without payroll access the
      // employee_private read returns null, so those fields simply render as "—".
      const [empRes, privRes] = await Promise.all([
        supabase
          .from("employees")
          .select(`
            employee_id, employee_guid, first_name, middle_name, last_name, preferred_name,
            email, mobile, home_phone,
            employment_type, ordinary_hours_per_week, primary_role_name, job_title, subcontractor_company_id,
            start_date, end_date, base_rate, lafha_eligible, lafha_rate,
            is_schedulable,
            profile_photo_url, is_active, created_at, updated_at,
            xero_contact_id, xero_contact_name, xero_linked_at,
            subcontractor_company:subcontractor_company_id (legal_name, trading_name)
          `)
          .eq("employee_id", employeeId!)
          .single(),
        supabase
          .from("employee_private")
          .select(`
            date_of_birth, address_line1, address_line2, suburb, state, postcode,
            next_of_kin_name, next_of_kin_relationship, next_of_kin_phone,
            emergency_medical_notes, blood_type,
            usi, tfn_on_file, super_fund_name, super_fund_usi, super_member_number,
            bank_bsb, bank_acct_number, xero_employee_id
          `)
          .eq("employee_id", employeeId!)
          .maybeSingle(),
      ]);
      if (empRes.error) throw empRes.error;
      return { ...(empRes.data as any), ...((privRes.data as any) ?? {}) } as unknown as EmployeeRow;
    },
  });

  if (isLoading) return <div className="p-8 flex justify-center"><Spinner/></div>;
  if (error || !emp) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card>
          <div className="p-6 text-[13px] text-text-muted">
            Employee not found, or you don't have permission to view this record.
          </div>
        </Card>
      </div>
    );
  }

  const fullName = [emp.first_name, emp.middle_name, emp.last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto space-y-4">
      <button
        type="button"
        onClick={() => navigate("/people")}
        className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-2 hover:text-text"
      >
        <ArrowLeft size={14}/> People
      </button>

      {/* Header */}
      <Card>
        <div className="p-5 flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-full bg-accent/15 text-accent flex items-center justify-center flex-shrink-0">
              <User size={20}/>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] text-text-muted">{emp.employee_guid}</span>
                {emp.is_active
                  ? <Pill label="Active" tone="ok" small/>
                  : <Pill label="Archived" tone="closed" small/>}
                {emp.lafha_eligible && <Pill label="LAFHA" tone="open" small/>}
              </div>
              <h1 className="text-[20px] font-semibold mt-0.5 truncate">{fullName}</h1>
              <div className="text-[12px] text-text-muted">
                {emp.employment_type.replace(/_/g, " ")}
                {emp.primary_role_name && <> · {emp.primary_role_name}</>}
                {emp.subcontractor_company && (
                  <> · {emp.subcontractor_company.trading_name ?? emp.subcontractor_company.legal_name}</>
                )}
              </div>
              {/* James, 22 Aug 2026: "easy copy-paste fields on the person's
                  name first and last, mobile and email - we use those all the
                  time when making bookings." One click, one field, on the
                  clipboard - no select-drag over a table cell. */}
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                <CopyChip label="First" value={emp.first_name} />
                <CopyChip label="Last"  value={emp.last_name} />
                {emp.mobile && <CopyChip label="Mobile" value={emp.mobile} mono />}
                {emp.email  && <CopyChip label="Email"  value={emp.email}  mono />}
              </div>
            </div>
          </div>
          {canManage && !editing && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="secondary" size="sm" icon={Edit3} onClick={() => setEditing(true)}>
                Edit
              </Button>
              {/* T-249 — assign one-off task. The old gate here was "active and
                  not a subcontractor", hard-coded in this one file. Migration 325
                  made that a column, seeded from exactly this rule, so the
                  behaviour is unchanged on day one and is now changeable per
                  person on the Employment card below. */}
              {emp.is_active && emp.is_schedulable && (
                <Button variant="secondary" size="sm" icon={Send} onClick={() => setAssignTaskOpen(true)}>
                  Assign task
                </Button>
              )}
              {/* T-MFA — lost/new phone. Clears their authenticator factors so
                  the setup screen runs again at their next login. Director only,
                  and the endpoint itself demands a code-verified session. */}
              {emp.is_active && isDirector && (
                <Button variant="secondary" size="sm" icon={Shield} disabled={mfaResetBusy}
                  onClick={resetMfa}>
                  {mfaResetBusy ? "Resetting…" : "Reset MFA"}
                </Button>
              )}
              {emp.is_active && isDirector && (
                <Button
                  variant="danger"
                  size="sm"
                  icon={Archive}
                  onClick={() => setArchiveConfirm(true)}
                >
                  Archive
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {!editing && (
        <>
          {/* At a glance, before the detail: is this person right for site.
              Same ring, same maths as their own portal - see the card's header
              comment. Only renders for people in site-compliance scope. */}
          <EmployeeReadinessCard employeeId={emp.employee_id} />
          <ReadOnlyView emp={emp} canEditXero={isDirector} onSaved={refetch} />
          {/* Migration 328 — the person's live leave position, and (director
              only) where their opening balance from Xero is entered. */}
          <LeavePositionCard employeeId={emp.employee_id} isDirector={isDirector} />
          <LinkedRecordsTiles employeeId={emp.employee_id} />
        </>
      )}

      {editing && (
        <EditForm
          emp={emp}
          onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); refetch(); }}
        />
      )}

      {archiveConfirm && (
        <ArchiveConfirmDialog
          emp={emp}
          onCancel={() => setArchiveConfirm(false)}
          onArchived={() => { setArchiveConfirm(false); refetch(); }}
        />
      )}

      {/* T-249 — assign ad-hoc task to this employee */}
      {assignTaskOpen && (
        <AssignPersonalBookingModal
          assigneeEmployeeId={emp.employee_id}
          assigneeName={fullName}
          onClose={() => setAssignTaskOpen(false)}
          onCreated={() => setAssignTaskOpen(false)}
        />
      )}
    </div>
  );
}

// =============================================================================
// READ-ONLY VIEW
// =============================================================================
function ReadOnlyView({ emp, canEditXero, onSaved }: { emp: EmployeeRow; canEditXero: boolean; onSaved: () => void }) {
  const fullAddress = [emp.address_line1, emp.address_line2, emp.suburb, emp.state, emp.postcode]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <Card>
        <div className="p-5">
          <SectionHeading icon={User}>Personal</SectionHeading>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-3 mt-3">
            <Kv label="Preferred name" value={emp.preferred_name ?? "—"} />
            <Kv label="Date of birth"  value={emp.date_of_birth ? fmtDate(emp.date_of_birth) : "—"} />
            <Kv label="Email"  value={emp.email  ?? "—"} mono />
            <Kv label="Mobile" value={emp.mobile ?? "—"} mono />
            <Kv label="Home phone" value={emp.home_phone ?? "—"} mono />
            <Kv label="Blood type" value={emp.blood_type ?? "—"} />
            <div className="md:col-span-2">
              <Kv label="Address" value={fullAddress || "—"} />
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-5">
          <SectionHeading icon={Heart}>Emergency</SectionHeading>
          <div className="grid md:grid-cols-3 gap-x-6 gap-y-3 mt-3">
            <Kv label="Next of kin"   value={emp.next_of_kin_name ?? "—"} />
            <Kv label="Relationship"  value={emp.next_of_kin_relationship ?? "—"} />
            <Kv label="Contact"       value={emp.next_of_kin_phone ?? "—"} mono />
          </div>
          {emp.emergency_medical_notes && (
            <div className="mt-3 pt-3 border-t border-line">
              <Kv label="Medical notes" value={emp.emergency_medical_notes} />
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="p-5">
          <SectionHeading icon={Briefcase}>Employment</SectionHeading>
          <div className="grid md:grid-cols-3 gap-x-6 gap-y-3 mt-3">
            <Kv label="Type"           value={emp.employment_type.replace(/_/g, " ")} />
            {/* Migration 328 — drives leave accrual and hours-per-day-of-leave. */}
            <Kv label="Ordinary week"  value={`${Number(emp.ordinary_hours_per_week ?? 38).toFixed(1)} hrs (${(Number(emp.ordinary_hours_per_week ?? 38) / 5).toFixed(2)} hrs/day of leave)`} mono />
            <Kv label="Job title"      value={emp.job_title ?? "—"} />
            <Kv label="Role"           value={emp.primary_role_name ?? "—"} />
            <Kv label="Start date"     value={emp.start_date ? fmtDate(emp.start_date) : "—"} />
            <Kv label="End date"       value={emp.end_date   ? fmtDate(emp.end_date)   : "—"} />
            {/* T-508 — pay rate is the single worst thing to have on screen
                during a share. Masks with everything else. */}
            <Kv label="Base rate"      value={emp.base_rate == null ? "—" : isPresenting() ? MONEY_MASK : `$${Number(emp.base_rate).toFixed(2)}/hr`} mono />
            <Kv label="LAFHA"          value={emp.lafha_eligible
              ? (isPresenting() ? `Eligible — ${MONEY_MASK}` : `Eligible — $${Number(emp.lafha_rate ?? 0).toFixed(0)}/night`)
              : "Not eligible"} />
            {/* James, 21 Aug 2026, on this card: "put it in here if you want."
                Whether somebody appears on the crew schedule at all. */}
            <Kv label="On the schedule" value={emp.is_schedulable
              ? "Yes — can be crewed and given tasks"
              : "No — hidden from the schedule"} />
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-5">
          <SectionHeading icon={Banknote}>Payroll &amp; Compliance</SectionHeading>
          <div className="grid md:grid-cols-3 gap-x-6 gap-y-3 mt-3">
            <Kv label="TFN on file" value={emp.tfn_on_file ? "Yes" : "—"} />
            <Kv label="USI"         value={emp.usi ?? "—"} mono />
            {/* G3 — this is the PAYROLL employee id, not the contact a bill is
                raised against. Labelled explicitly because the two were being
                confused, and this one has never been populated. */}
            <Kv label="Xero payroll ID" value={emp.xero_employee_id ?? "—"} mono />
            <Kv label="Super fund"  value={emp.super_fund_name ?? "—"} />
            <Kv label="Super USI"   value={emp.super_fund_usi  ?? "—"} mono />
            <Kv label="Member #"    value={emp.super_member_number ?? "—"} mono />
            <Kv label="Bank BSB"    value={emp.bank_bsb ?? "—"} mono />
            <Kv label="Account #"   value={emp.bank_acct_number ? maskAccount(emp.bank_acct_number) : "—"} mono />
          </div>
        </div>
      </Card>

      {/* G3 — the Xero CONTACT this person is reimbursed through. Separate card
          because it is a different object from everything above it: the fields
          above are payroll, this is who a bill is raised against. */}
      <XeroContactCard emp={emp} canEdit={canEditXero} onSaved={onSaved} />
    </>
  );
}

// =============================================================================
// XeroContactCard — link an employee to their Xero CONTACT
// =============================================================================
// Needed before reimbursements can be bundled into one bill per person
// (claude/2026-08-18_TICKET_bundle_employee_reimbursements.md). A bill is raised
// against a Contact, not against the payroll Employee record, which is why this
// is not the "Xero payroll ID" field above.
//
// This reuses the SAME picker pattern the Clients screen has had since T-504,
// reading GET /xero/contacts on the api-gateway Worker. That endpoint already
// exists and is already deployed - nothing here changes the Worker, the Xero
// app, or the OAuth connection.
//
// The saving side deliberately does NOT go through the Worker. Clients post to
// /xero/contacts/link because that endpoint writes to public.clients. For an
// employee the write is a plain update on public.employees, which the director
// can already do under RLS - so adding a Worker route (and a deploy, and a new
// way for the Worker to touch the employees table) would buy nothing.
//
// Nothing is created in Xero from here. James sets the contact up in Xero and
// links to it - an integration that invents contacts in the accounting system
// is how ledgers get messy.
// =============================================================================
function XeroContactCard({
  emp, canEdit, onSaved,
}: { emp: any; canEdit: boolean; onSaved: () => void }) {
  const [picking, setPicking] = useState(false);
  const [manual, setManual]   = useState(false);
  const [cid, setCid]         = useState(emp.xero_contact_id ?? "");
  const [cname, setCname]     = useState(emp.xero_contact_name ?? "");
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState<string | null>(null);

  const linked = !!emp.xero_contact_id;

  // One place that writes the link, whether it came from the picker or from a
  // hand-typed id. clear = true unlinks.
  async function save(next: { id: string | null; name: string | null }, clear = false) {
    setBusy(true); setErr(null);
    const { error } = await supabase
      .from("employees")
      .update({
        xero_contact_id:   clear ? null : (next.id?.trim() || null),
        xero_contact_name: clear ? null : (next.name?.trim() || null),
        xero_linked_at:    clear || !next.id?.trim() ? null : new Date().toISOString(),
      })
      .eq("employee_id", emp.employee_id);
    setBusy(false);

    if (error) {
      // employees_xero_contact_id_uidx (migration 308). Postgres says "duplicate
      // key value violates unique constraint", which tells nobody anything.
      if ((error as any).code === "23505") {
        setErr("Another employee is already linked to that Xero contact. Unlink them first, or pick a different contact.");
      } else {
        setErr(error.message);
      }
      return;
    }

    if (clear) { setCid(""); setCname(""); }
    setPicking(false);
    setManual(false);
    onSaved();
  }

  return (
    <Card>
      <div className="p-5">
        <SectionHeading icon={Banknote}>Xero contact</SectionHeading>
        <p className="text-[11px] text-text-muted mt-1">
          Who a reimbursement bill is raised against in Xero. This is a Contact, not the
          payroll employee record. The contact has to exist in Xero first — this only
          records which one it is.
        </p>

        {!manual && (
          <div className="mt-3 flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              {linked ? (
                <div className="flex items-start gap-2">
                  <CheckCircle2 size={15} className="text-ok mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-text text-sm">
                      Linked to <span className="font-medium">{emp.xero_contact_name ?? "a Xero contact"}</span>
                    </div>
                    <div className="text-[11px] text-text-dim font-mono mt-0.5 break-all">{emp.xero_contact_id}</div>
                    {emp.xero_linked_at && (
                      <div className="text-[11px] text-text-dim mt-0.5">Linked {fmtDate(emp.xero_linked_at)}</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <AlertTriangle size={15} className="text-warn mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-text text-sm">Not linked to Xero</div>
                    <div className="text-[11px] text-text-dim mt-0.5">
                      Reimbursements for this person can't be billed to Xero until they are.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {canEdit && (
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                <Button variant="secondary" size="sm" icon={Search}
                        onClick={() => { setErr(null); setPicking(true); }} disabled={busy}>
                  {linked ? "Change" : "Find in Xero"}
                </Button>
                {linked && (
                  <Button variant="danger" size="sm"
                          onClick={() => save({ id: null, name: null }, true)} disabled={busy}>
                    Unlink
                  </Button>
                )}
                <button
                  onClick={() => { setErr(null); setCid(emp.xero_contact_id ?? ""); setCname(emp.xero_contact_name ?? ""); setManual(true); }}
                  className="text-[11px] text-text-muted hover:text-text underline">
                  Enter ID by hand
                </button>
              </div>
            )}
          </div>
        )}

        {/* Kept as a fallback. If Xero is disconnected or the contact list won't
            load, the link can still be made rather than the job being blocked. */}
        {manual && (
          <div className="mt-3 space-y-3 max-w-lg">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-text-muted">Xero contact ID</span>
              <input value={cid} onChange={e => setCid(e.target.value)}
                placeholder="Paste from Xero"
                className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text font-mono" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-text-muted">Contact name</span>
              <input value={cname} onChange={e => setCname(e.target.value)}
                placeholder="As it reads in Xero"
                className="mt-1 block w-full bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text" />
              <span className="text-[11px] text-text-dim">
                Shown here so you can see who it points at. The ID is what actually matters.
              </span>
            </label>
            {err && <div className="text-[11px] text-danger">{err}</div>}
            <div className="flex items-center gap-2">
              <Button variant="primary" size="sm"
                      onClick={() => save({ id: cid, name: cname })} disabled={busy}>
                {busy ? "Saving…" : "Save"}
              </Button>
              <Button variant="secondary" size="sm"
                      onClick={() => { setManual(false); setErr(null); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {err && !manual && (
          <div className="mt-3 text-[12px] text-danger bg-danger/10 border border-danger/30 rounded px-2.5 py-2">
            {err}
          </div>
        )}

        {picking && (
          <XeroEmployeeContactPicker
            employeeName={[emp.first_name, emp.last_name].filter(Boolean).join(" ")}
            currentContactId={emp.xero_contact_id ?? null}
            busy={busy}
            onPick={(id, name) => save({ id, name })}
            onClose={() => { setPicking(false); setErr(null); }}
          />
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// XeroEmployeeContactPicker
//
// Reads Xero's live contact list rather than a stored copy - a contact added in
// Xero five minutes ago has to be selectable, and there is no version of this
// where a stale mirror is the safer option.
//
// GET /xero/contacts returns linked_to, which is the CLIENT (if any) already
// pointing at that contact. A contact that is somebody's customer record should
// not also become an employee's reimbursement payee, so those rows stay
// unselectable and say why.
// -----------------------------------------------------------------------------
function XeroEmployeeContactPicker({
  employeeName, currentContactId, busy, onPick, onClose,
}: {
  employeeName: string;
  currentContactId: string | null;
  busy: boolean;
  onPick: (id: string, name: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState(employeeName.split(/\s+/)[0] ?? "");
  const [rows, setRows] = useState<any[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRows(null); setLoadError(null);
      try {
        const res = await apiFetch(`/xero/contacts?q=${encodeURIComponent(q)}`, { method: "GET" });
        const j = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) { setLoadError(j?.detail ?? j?.error ?? `Couldn't read Xero contacts (${res.status})`); return; }
        setRows(j.contacts ?? []);
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message ?? String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [q]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-[560px] mt-16"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div className="text-sm font-semibold text-text">
            Link {employeeName || "this employee"} to a Xero contact
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text"><X size={16} /></button>
        </div>

        <div className="p-4 space-y-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Xero contacts…" />

          {loadError && <div className="text-[12px] text-danger">{loadError}</div>}

          {rows === null && !loadError && (
            <div className="py-8 text-center text-[12px] text-text-muted italic">Reading Xero…</div>
          )}

          {rows && rows.length === 0 && (
            <div className="py-8 text-center text-[12px] text-text-muted italic">
              No Xero contact matches “{q}”. Add them in Xero first, then come back.
            </div>
          )}

          {rows && rows.length > 0 && (
            <div className="max-h-[380px] overflow-y-auto divide-y divide-line border border-line rounded">
              {rows.map((r: any) => {
                const takenByClient = !!r.linked_to && r.xero_contact_id !== currentContactId;
                return (
                  <button
                    key={r.xero_contact_id}
                    disabled={busy || takenByClient}
                    onClick={() => onPick(r.xero_contact_id, r.name)}
                    className="w-full text-left px-3 py-2.5 hover:bg-ink-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] text-text">{r.name}</span>
                      {r.is_supplier && <Pill label="supplier" tone="default" small />}
                      {r.is_customer && <Pill label="customer" tone="ok" small />}
                    </div>
                    <div className="text-[11px] text-text-dim mt-0.5">
                      {r.xero_contact_id === currentContactId
                        ? "Currently linked — pick it again to refresh the stored name"
                        : takenByClient
                          ? `Already used as the client ${r.linked_to}`
                          : (r.email ?? "No email on the Xero contact")}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// LINKED RECORDS — stat tiles with click-through to dedicated screens
// =============================================================================
function LinkedRecordsTiles({ employeeId }: { employeeId: string }) {
  // Pull lightweight counts for the stat tiles. Each is a separate query so a
  // failure on one doesn't blank the others.
  const { data: licences } = useQuery({
    queryKey: ["emp_licences_count", employeeId],
    queryFn: async () => {
      const { data } = await supabase
        .from("employee_licences")
        .select("licence_id, expiry_date", { count: "exact" })
        .eq("employee_id", employeeId);
      return data ?? [];
    },
  });

  // Migration 328 — the LIVE position (opening + accrual − taken), same view as
  // My Leave and Leave approvals. The old query read the raw anchor table,
  // which nobody ever kept current.
  const { data: leavePosition } = useQuery({
    queryKey: ["emp_leave_position", employeeId],
    queryFn: async () => {
      const { data } = await supabase
        .from("v_leave_position" as any)
        .select("has_opening, al_days, sl_days, lsl_weeks, rdo_days")
        .eq("employee_id", employeeId)
        .maybeSingle();
      return data as unknown as { has_opening: boolean; al_days: number; sl_days: number; lsl_weeks: number; rdo_days: number } | null;
    },
  });

  const { data: projects } = useQuery({
    queryKey: ["emp_project_history", employeeId],
    queryFn: async () => {
      const { data } = await supabase
        .from("project_team")
        .select(`
          assignment_id, role_on_project, assigned_from, assigned_to,
          project:project_id (project_id, project_code, project_name, status)
        `)
        .eq("employee_id", employeeId)
        .order("assigned_from", { ascending: false })
        .limit(10);
      return (data ?? []) as any[];
    },
  });

  const { data: crews } = useQuery({
    queryKey: ["emp_crew_membership", employeeId],
    queryFn: async () => {
      const { data } = await supabase
        .from("crew_members")
        .select(`
          crew_member_id, crew_role, member_from, member_to,
          crew:crew_id (crew_id, crew_code, crew_name, colour_hex)
        `)
        .eq("employee_id", employeeId)
        .is("member_to", null);
      return (data ?? []) as any[];
    },
  });

  // 333 — the employee's SITE HISTORY, off the roster. This used to be
  // inferred from "crews they're in today × those crews' bookings" (T-450b),
  // which vanished the moment a membership was edited - Ken's Walcha stint,
  // gone by drag-and-drop. site_roster is the record: seeded when a crew is
  // booked, owned per person, and it survives crew moves. Booking deleted
  // after works started? The row stays (its pca just goes null).
  const { data: rosterStints } = useQuery({
    queryKey: ["emp_roster_stints", employeeId],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("site_roster")
        .select(`
          roster_id, on_site_from, on_site_to, status, role_on_site,
          project:project_id (project_id, project_code, project_name, status,
                              sites:site_id (site_name)),
          booking:pca_id (assigned_to, crews:crew_id (crew_code))
        `)
        .eq("employee_id", employeeId)
        .order("on_site_from", { ascending: false })
        .limit(20);
      return (data ?? []) as any[];
    },
  });

  const today = new Date().toISOString().slice(0, 10);
  const expiringSoon = (licences ?? []).filter(l =>
    l.expiry_date && l.expiry_date > today && l.expiry_date <= addDaysISO(today, 90)
  ).length;
  const expired = (licences ?? []).filter(l =>
    l.expiry_date && l.expiry_date <= today
  ).length;

  // An employee is "on" a project if assigned individually (project_team) OR
  // rostered on a site visit (site_roster, 333). Merge both, dedupe by project
  // (an individual assignment wins the label over the roster one). Each roster
  // stint carries its own dates - THIS is the history that survives crew moves.
  // Future stints stay out of here; they belong to "Coming up" below.
  const combinedProjects = (() => {
    const rows: any[] = [];
    for (const a of (projects ?? [])) {
      if (a.project?.project_id) rows.push({
        key: `t-${a.assignment_id}`, project: a.project,
        via: a.role_on_project ? `as ${a.role_on_project}` : "individual",
        from: a.assigned_from, to: a.assigned_to, listed: false,
      });
    }
    for (const a of (rosterStints ?? [])) {
      if (!a.project?.project_id) continue;
      if (a.on_site_from > today) continue;   // future stint → Coming up
      rows.push({
        key: `r-${a.roster_id}`, project: a.project,
        via: [
          a.booking?.crews?.crew_code ? `with ${a.booking.crews.crew_code}` : "on site",
          a.role_on_site || null,
          a.status === "listed" ? "listed" : null,
        ].filter(Boolean).join(" · "),
        from: a.on_site_from,
        to: a.on_site_to ?? a.booking?.assigned_to ?? null,
        listed: a.status === "listed",
      });
    }
    const byId = new Map<string, any>();
    for (const r of rows) { if (!byId.has(r.project.project_id)) byId.set(r.project.project_id, r); }
    return Array.from(byId.values())
      .sort((a, b) => String(b.from ?? "").localeCompare(String(a.from ?? "")));
  })();

  return (
    <Card>
      <div className="p-5">
        <SectionHeading icon={FolderKanban}>Linked records</SectionHeading>

        <div className="grid md:grid-cols-4 gap-3 mt-3">
          <StatTile
            label="Licences"
            value={(licences?.length ?? 0).toString()}
            sub={
              expired > 0 ? `${expired} expired`
              : expiringSoon > 0 ? `${expiringSoon} expiring 90d`
              : (licences?.length ? "all current" : "—")
            }
            tone={expired > 0 ? "danger" : expiringSoon > 0 ? "warn" : "ok"}
            // James, 22 Aug 2026: "make these navigate not to just the licence
            // screen but [this person's] licences." /hr/licences/:employeeId is
            // the director/manager view of ONE person's tickets - the tile was
            // dumping you at the whole-company board and making you find them
            // again.
            href={`/hr/licences/${employeeId}`}
          />
          <StatTile
            label="Annual leave"
            value={leavePosition?.al_days != null
              ? `${Number(leavePosition.al_days).toFixed(1)}d` : "—"}
            sub="live balance"
          />
          <StatTile
            label="Project history"
            value={combinedProjects.length.toString()}
            sub={combinedProjects.length > 0
              ? `latest ${combinedProjects[0]?.project?.project_code ?? "—"}`
              : "no projects"}
          />
          <StatTile
            label="Crews"
            value={(crews?.length ?? 0).toString()}
            sub={crews && crews.length > 0
              ? crews.map(c => c.crew?.crew_code).filter(Boolean).join(", ")
              : "not in a crew"}
            href="/crews"
          />
        </div>

        {/* ---- Coming up -------------------------------------------------
            James, 22 Aug 2026: "we should add the employee's schedule, not just
            past works." Everything AHEAD for this person, from the same three
            sources the schedule reads: their ROSTER stints (333 - what they're
            actually rostered on, not everything their crew booked), their
            one-off tasks, and defect visits assigned to them. Next 60 days,
            soonest first, capped - this is a card, not the schedule. */}
        <ComingUp employeeId={employeeId} />

        {/* Recent projects — individual (project_team) + roster stints (333).
            The roster rows are the record that survives crew moves and booking
            deletes - Ken's Walcha stint lives here now, permanently. */}
        {combinedProjects.length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Recent projects</div>
            <div className="space-y-1.5">
              {combinedProjects.slice(0, 5).map((a: any) => (
                <Link
                  key={a.key}
                  to={`/projects/${a.project?.project_id}`}
                  className={`flex items-center justify-between gap-3 bg-ink rounded px-3 py-2 hover:border-accent border transition-colors ${
                    a.listed ? "border-dashed border-line" : "border-line/50"}`}
                >
                  <div className="min-w-0">
                    <div className="text-[12px] font-mono text-accent truncate">{a.project?.project_code ?? "—"}</div>
                    <div className="text-[11px] text-text-muted truncate">
                      {a.project?.project_name ?? "—"} · {a.via}
                    </div>
                  </div>
                  <div className="text-[10px] text-text-muted font-mono whitespace-nowrap">
                    {fmtDate(a.from)}{a.to ? ` → ${fmtDate(a.to)}` : ""}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// =============================================================================
// ComingUp — what is AHEAD for one person: crew bookings, one-off tasks, defect
// visits. The mirror of "Recent projects", which only ever looked backwards.
// =============================================================================
function ComingUp({ employeeId }: { employeeId: string }) {
  const today   = new Date().toISOString().slice(0, 10);
  const horizon = addDaysISO(today, 60);

  const { data: items = [] } = useQuery({
    queryKey: ["emp_coming_up", employeeId, today],
    queryFn: async () => {
      type Up = { key: string; kind: "crew" | "task" | "defect";
                  from: string; to: string | null; what: string; where: string };
      const out: Up[] = [];

      // 333 — their ROSTER stints still running or yet to start. Per person,
      // not per crew: only visits they are actually rostered (or listed) on.
      {
        const { data } = await (supabase as any)
          .from("site_roster")
          .select(`roster_id, on_site_from, on_site_to, status,
                   project:project_id ( project_name, sites:site_id ( site_name ) ),
                   booking:pca_id ( assigned_to, crews:crew_id ( crew_code ) )`)
          .eq("employee_id", employeeId)
          .or(`on_site_to.is.null,on_site_to.gte.${today}`)
          .lte("on_site_from", horizon);
        for (const a of (data ?? []) as any[]) {
          out.push({
            key: `c-${a.roster_id}`, kind: "crew",
            from: a.on_site_from,
            to: a.on_site_to ?? a.booking?.assigned_to ?? null,
            what: `${a.status === "listed" ? "Listed" : "On site"}${
              a.booking?.crews?.crew_code ? ` · ${a.booking.crews.crew_code}` : ""}`,
            where: a.project?.sites?.site_name ?? a.project?.project_name ?? "site",
          });
        }
      }

      const { data: tasks } = await supabase
        .from("personal_bookings")
        .select("booking_id, title, scheduled_start, scheduled_end, location_address, status")
        .eq("assignee_employee_id", employeeId)
        .not("status", "in", "(cancelled,done)")
        .gte("scheduled_start", `${today}T00:00:00`)
        .lte("scheduled_start", `${horizon}T23:59:59`);
      for (const t of (tasks ?? []) as any[]) {
        out.push({
          key: `t-${t.booking_id}`, kind: "task",
          from: t.scheduled_start.slice(0, 10),
          to: t.scheduled_end ? t.scheduled_end.slice(0, 10) : null,
          what: t.title, where: t.location_address ?? "",
        });
      }

      const { data: defects } = await supabase
        .from("site_defects")
        .select("defect_id, defect_number, title, due_date, sites:site_id ( site_name )")
        .eq("assigned_to_employee_id", employeeId)
        .in("status", ["open", "in_progress", "awaiting_reinspection"])
        .not("due_date", "is", null)
        .gte("due_date", today);
      for (const d of (defects ?? []) as any[]) {
        out.push({
          key: `d-${d.defect_id}`, kind: "defect",
          from: d.due_date, to: d.due_date,
          what: `Defect visit · ${d.defect_number}`,
          where: d.sites?.site_name ?? d.title,
        });
      }

      return out.sort((a, b) => a.from.localeCompare(b.from)).slice(0, 6);
    },
  });

  if (items.length === 0) return null;

  const KIND_TONE: Record<string, string> = {
    crew:   "bg-accent/10 text-accent",
    task:   "bg-[rgba(255,120,68,0.14)] text-[rgb(200,90,40)]",
    defect: "bg-[rgba(220,68,68,0.12)] text-[rgb(190,60,60)]",
  };

  return (
    <div className="mt-5">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Coming up</div>
      <div className="space-y-1.5">
        {items.map(i => (
          <div key={i.key}
            className="flex items-center justify-between gap-3 bg-ink rounded px-3 py-2 border border-line/50">
            <div className="min-w-0 flex items-center gap-2">
              <span className={`text-[9px] uppercase font-mono px-1.5 py-0.5 rounded flex-shrink-0 ${KIND_TONE[i.kind]}`}>
                {i.kind}
              </span>
              <div className="min-w-0">
                <div className="text-[12px] text-text truncate">{i.what}</div>
                {i.where && <div className="text-[11px] text-text-muted truncate">{i.where}</div>}
              </div>
            </div>
            <div className="text-[10px] text-text-muted font-mono whitespace-nowrap">
              {fmtDate(i.from)}{i.to && i.to !== i.from ? ` → ${fmtDate(i.to)}` : ""}{!i.to && i.kind === "crew" ? " → open" : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// EDIT FORM — manager+ only. Single big form, save in one transaction.
// =============================================================================
function EditForm({
  emp, onCancel, onSaved,
}: {
  emp: EmployeeRow;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState({ ...emp });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // T-453 — Job title/primary role is FK-constrained to roles; load the valid list.
  const { data: roles = [] } = useQuery({
    queryKey: ["employee_roles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roles").select("role_name, display_name, sort_order").order("sort_order");
      if (error) throw error;
      return (data ?? []) as { role_name: string; display_name: string | null }[];
    },
  });

  function set<K extends keyof EmployeeRow>(key: K, value: EmployeeRow[K]) {
    setF(prev => ({ ...prev, [key]: value }));
  }

  async function save() {
    setBusy(true); setErr(null);
    try {
      const payload: any = {
        first_name:     f.first_name,
        middle_name:    f.middle_name || null,
        last_name:      f.last_name,
        preferred_name: f.preferred_name || null,
        date_of_birth:  f.date_of_birth || null,
        email:          f.email || null,
        mobile:         f.mobile || null,
        home_phone:     f.home_phone || null,
        address_line1:  f.address_line1 || null,
        address_line2:  f.address_line2 || null,
        suburb:         f.suburb || null,
        state:          f.state || null,
        postcode:       f.postcode || null,
        next_of_kin_name:         f.next_of_kin_name || null,
        next_of_kin_relationship: f.next_of_kin_relationship || null,
        next_of_kin_phone:        f.next_of_kin_phone || null,
        emergency_medical_notes:  f.emergency_medical_notes || null,
        blood_type:     f.blood_type || null,
        employment_type: f.employment_type,
        ordinary_hours_per_week: f.ordinary_hours_per_week != null && String(f.ordinary_hours_per_week).trim() !== ""
          ? Number(f.ordinary_hours_per_week) : 38,
        primary_role_name: f.primary_role_name || null,
        job_title:         f.job_title || null,
        start_date:    f.start_date || null,
        end_date:      f.end_date || null,
        base_rate:     f.base_rate != null && String(f.base_rate).trim() !== "" ? Number(f.base_rate) : null,
        lafha_eligible: f.lafha_eligible,
        is_schedulable: f.is_schedulable,
        lafha_rate:    f.lafha_rate != null && String(f.lafha_rate).trim() !== "" ? Number(f.lafha_rate) : null,
        usi:           f.usi || null,
        tfn_on_file:   f.tfn_on_file,
        super_fund_name:     f.super_fund_name || null,
        super_fund_usi:      f.super_fund_usi  || null,
        super_member_number: f.super_member_number || null,
        bank_bsb:           f.bank_bsb || null,
        bank_acct_number:   f.bank_acct_number || null,
      };
      // T-446 — sensitive fields moved to employee_private; route them through the
      // update_employee_profile RPC (handles the director/payroll gating). Everything
      // that stays on employees (name, contact, employment, pay) is a direct update.
      const SENSITIVE = new Set([
        "date_of_birth", "address_line1", "address_line2", "suburb", "state", "postcode",
        "next_of_kin_name", "next_of_kin_relationship", "next_of_kin_phone",
        "emergency_medical_notes", "blood_type", "usi", "tfn_on_file",
        "super_fund_name", "super_fund_usi", "super_member_number", "bank_bsb", "bank_acct_number",
      ]);
      const empPayload: any = {}, sensitivePayload: any = {};
      for (const k of Object.keys(payload)) (SENSITIVE.has(k) ? sensitivePayload : empPayload)[k] = payload[k];

      const { error: e1 } = await supabase.from("employees").update(empPayload).eq("employee_id", emp.employee_id);
      if (e1) throw e1;
      const { error: e2 } = await supabase.schema("app").rpc("update_employee_profile", {
        p_employee_id: emp.employee_id,
        p_payload: sensitivePayload,
      });
      if (e2) throw e2;
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="p-5 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-[15px] font-semibold inline-flex items-center gap-2">
            <Edit3 size={14}/> Edit employee
          </h3>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" icon={X} onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button size="sm" icon={Save} disabled={busy} onClick={save}>
              {busy ? <Spinner size={12}/> : "Save"}
            </Button>
          </div>
        </div>

        {err && (
          <div className="text-[12px] text-lost border border-lost/30 bg-lost/[0.05] rounded px-3 py-2 inline-flex items-start gap-2">
            <AlertTriangle size={12} className="mt-0.5"/> {err}
          </div>
        )}

        {/* Personal */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Personal</div>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="First name *">
              <Input value={f.first_name} onChange={e => set("first_name", e.target.value)} />
            </Field>
            <Field label="Middle name">
              <Input value={f.middle_name ?? ""} onChange={e => set("middle_name", e.target.value)} />
            </Field>
            <Field label="Last name *">
              <Input value={f.last_name} onChange={e => set("last_name", e.target.value)} />
            </Field>
            <Field label="Preferred name">
              <Input value={f.preferred_name ?? ""} onChange={e => set("preferred_name", e.target.value)} />
            </Field>
            <Field label="Date of birth">
              <Input type="date" value={f.date_of_birth ?? ""} onChange={e => set("date_of_birth", e.target.value)} />
            </Field>
            <Field label="Blood type">
              <Input value={f.blood_type ?? ""} onChange={e => set("blood_type", e.target.value)} />
            </Field>
          </div>
        </div>

        {/* Contact + address */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Contact &amp; address</div>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Email">
              <Input type="email" value={f.email ?? ""} onChange={e => set("email", e.target.value)} />
            </Field>
            <Field label="Mobile">
              <Input mono value={f.mobile ?? ""} onChange={e => set("mobile", e.target.value)} />
            </Field>
            <Field label="Home phone">
              <Input mono value={f.home_phone ?? ""} onChange={e => set("home_phone", e.target.value)} />
            </Field>
            <Field label="Address line 1">
              <Input value={f.address_line1 ?? ""} onChange={e => set("address_line1", e.target.value)} />
            </Field>
            <Field label="Address line 2">
              <Input value={f.address_line2 ?? ""} onChange={e => set("address_line2", e.target.value)} />
            </Field>
            <Field label="Suburb">
              <Input value={f.suburb ?? ""} onChange={e => set("suburb", e.target.value)} />
            </Field>
            <Field label="State">
              <Input value={f.state ?? ""} onChange={e => set("state", e.target.value)} />
            </Field>
            <Field label="Postcode">
              <Input mono value={f.postcode ?? ""} onChange={e => set("postcode", e.target.value)} />
            </Field>
          </div>
        </div>

        {/* Emergency */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Emergency &amp; medical</div>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Next of kin name">
              <Input value={f.next_of_kin_name ?? ""} onChange={e => set("next_of_kin_name", e.target.value)} />
            </Field>
            <Field label="Relationship">
              <Input value={f.next_of_kin_relationship ?? ""} onChange={e => set("next_of_kin_relationship", e.target.value)} />
            </Field>
            <Field label="Contact phone">
              <Input mono value={f.next_of_kin_phone ?? ""} onChange={e => set("next_of_kin_phone", e.target.value)} />
            </Field>
            <div className="md:col-span-3">
              <Field label="Medical notes" hint="Visible to supervisors in an incident.">
                <Textarea
                  rows={2}
                  value={f.emergency_medical_notes ?? ""}
                  onChange={e => set("emergency_medical_notes", e.target.value)}
                />
              </Field>
            </div>
          </div>
        </div>

        {/* Employment */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Employment</div>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Type *">
              <Select value={f.employment_type} onChange={e => set("employment_type", e.target.value as EmpType)}>
                <option value="full_time">Full time</option>
                <option value="part_time">Part time</option>
                <option value="casual">Casual</option>
                <option value="subcontractor">Subcontractor</option>
                <option value="labour_hire">Labour hire</option>
              </Select>
            </Field>
            <Field label="Primary role" hint="Their role/position. App access (what they can open) is set separately in Admin → Users.">
              <Select
                value={f.primary_role_name ?? ""}
                onChange={e => set("primary_role_name", (e.target.value || null) as any)}
              >
                <option value="">— none —</option>
                {roles.map(r => (
                  <option key={r.role_name} value={r.role_name}>{r.display_name ?? r.role_name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Job title" hint="Free-text position, e.g. Lead Rigger.">
              <Input
                value={f.job_title ?? ""}
                onChange={e => set("job_title", (e.target.value || null) as any)}
                placeholder="e.g. Lead Rigger, Tech, Apprentice"
              />
            </Field>
            <Field label="Ordinary hours / week"
              hint="Their award week — 38 for full-time. Leave accrues against this, and a day of leave is this ÷ 5.">
              <Input
                type="number" step="0.5" min="1" max="60" mono
                value={f.ordinary_hours_per_week == null ? "" : String(f.ordinary_hours_per_week)}
                onChange={e => set("ordinary_hours_per_week", e.target.value as any)}
                placeholder="38"
              />
            </Field>
            <Field label="Start date">
              <Input type="date" value={f.start_date ?? ""} onChange={e => set("start_date", e.target.value)} />
            </Field>
            <Field label="End date">
              <Input type="date" value={f.end_date ?? ""} onChange={e => set("end_date", e.target.value)} />
            </Field>
            <Field label="Base rate ($/hr)">
              <Input
                type="number"
                step="0.01"
                mono
                value={f.base_rate == null ? "" : String(f.base_rate)}
                onChange={e => set("base_rate", e.target.value as any)}
              />
            </Field>
            <Field label="LAFHA">
              <div className="flex items-center gap-3 h-[42px]">
                <label className="inline-flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={f.lafha_eligible}
                    onChange={e => set("lafha_eligible", e.target.checked)}
                  />
                  Eligible
                </label>
                {f.lafha_eligible && (
                  <Input
                    type="number"
                    step="0.01"
                    mono
                    value={f.lafha_rate == null ? "" : String(f.lafha_rate)}
                    onChange={e => set("lafha_rate", e.target.value as any)}
                    placeholder="$/night"
                    className="!w-[120px]"
                  />
                )}
              </div>
            </Field>
            <Field label="On the schedule"
              hint="Show this person on the crew schedule, so they can be put in a crew and given one-off tasks. Turn off for office staff and anyone who never attends site.">
              <div className="flex items-center gap-3 h-[42px]">
                <label className="inline-flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={f.is_schedulable}
                    onChange={e => set("is_schedulable", e.target.checked)}
                  />
                  Can be scheduled
                </label>
              </div>
            </Field>
          </div>
        </div>

        {/* Payroll & compliance */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Payroll &amp; compliance</div>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="TFN on file">
              <label className="inline-flex items-center gap-2 text-[13px] h-[42px]">
                <input
                  type="checkbox"
                  checked={f.tfn_on_file}
                  onChange={e => set("tfn_on_file", e.target.checked)}
                />
                Yes — TFN declaration received
              </label>
            </Field>
            <Field label="USI">
              <Input mono value={f.usi ?? ""} onChange={e => set("usi", e.target.value)} />
            </Field>
            <Field label="Xero employee ID" hint="From Xero — links payroll exports.">
              <Input mono value={f.xero_employee_id ?? ""} disabled />
            </Field>
            <Field label="Super fund">
              <Input value={f.super_fund_name ?? ""} onChange={e => set("super_fund_name", e.target.value)} />
            </Field>
            <Field label="Super USI">
              <Input mono value={f.super_fund_usi ?? ""} onChange={e => set("super_fund_usi", e.target.value)} />
            </Field>
            <Field label="Member number">
              <Input mono value={f.super_member_number ?? ""} onChange={e => set("super_member_number", e.target.value)} />
            </Field>
            <Field label="Bank BSB">
              <Input mono value={f.bank_bsb ?? ""} onChange={e => set("bank_bsb", e.target.value)} placeholder="000-000" />
            </Field>
            <Field label="Account number">
              <Input mono value={f.bank_acct_number ?? ""} onChange={e => set("bank_acct_number", e.target.value)} />
            </Field>
          </div>
        </div>
      </div>
    </Card>
  );
}

// =============================================================================
// ARCHIVE CONFIRMATION
// =============================================================================
function ArchiveConfirmDialog({
  emp, onCancel, onArchived,
}: {
  emp: EmployeeRow;
  onCancel: () => void;
  onArchived: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);

  async function archive() {
    setBusy(true); setErr(null);
    try {
      const { error } = await supabase
        .from("employees")
        .update({
          is_active: false,
          end_date:  emp.end_date ?? today,
        })
        .eq("employee_id", emp.employee_id);
      if (error) throw error;
      onArchived();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={busy ? undefined : onCancel}>
      <div className="bg-ink-card border border-line rounded-lg shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <Archive size={16} className="text-warn"/>
          <h3 className="text-[15px] font-semibold">Archive employee?</h3>
        </div>
        <p className="text-[13px] text-text-muted mb-3">
          {emp.first_name} {emp.last_name} will be marked inactive
          {emp.end_date ? ` (end date ${fmtDate(emp.end_date)})` : ` (end date ${fmtDate(today)})`}.
          History stays — projects, leave, licences, policy acknowledgements all
          remain on file. They can be reactivated by clicking "Show inactive" on
          the People list and editing.
        </p>
        <p className="text-[12px] text-text-muted mb-4 italic">
          If they have an app login, you'll also want to deactivate their user
          row in <span className="font-mono">/admin/users</span>.
        </p>
        {err && (
          <div className="mb-3 text-[12px] text-lost border border-lost/30 bg-lost/[0.05] rounded px-3 py-2">{err}</div>
        )}
        <div className="flex justify-end gap-2 pt-3 border-t border-line">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="danger" icon={Archive} disabled={busy} onClick={archive}>
            {busy ? "Archiving…" : "Archive"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// HELPERS
// =============================================================================
function SectionHeading({ icon: Icon, children }: { icon: any; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-[12px] uppercase tracking-wider text-text-muted">
      <Icon size={13}/> {children}
    </div>
  );
}

function Kv({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`mt-0.5 text-[13px] ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

// One click puts the value on the clipboard, and the chip says so for a moment.
// The label stays put so a row of these reads as a form, not a mystery.
function CopyChip({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch { /* clipboard blocked - the value is still visible to select */ }
      }}
      title={`Copy "${value}"`}
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-[11px] transition-colors ${
        copied ? "border-ok/60 bg-ok/10 text-ok" : "border-line hover:border-accent text-text"}`}
    >
      <span className="text-[9px] uppercase text-text-dim">{label}</span>
      <span className={`max-w-[180px] truncate ${mono ? "font-mono" : ""}`}>{value}</span>
      {copied ? <Check size={10} className="flex-shrink-0"/> : <CopyIcon size={10} className="flex-shrink-0 text-text-dim"/>}
    </button>
  );
}

function StatTile({
  label, value, sub, tone, href,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "ok" | "warn" | "danger";
  href?: string;
}) {
  const toneCls =
    tone === "danger" ? "border-danger/40 bg-danger/[0.05]" :
    tone === "warn"   ? "border-warn/40 bg-warn/[0.05]" :
    "border-line";
  const inner = (
    <div className={`p-3 rounded-md border ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className="text-[18px] font-semibold mt-1">{value}</div>
      {sub && <div className="text-[11px] text-text-muted mt-0.5 truncate">{sub}</div>}
    </div>
  );
  return href ? <Link to={href} className="hover:opacity-90 transition-opacity">{inner}</Link> : inner;
}

function maskAccount(acct: string): string {
  if (!acct) return "—";
  if (acct.length <= 4) return acct;
  return "••••" + acct.slice(-4);
}

function addDaysISO(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// =============================================================================
// LEAVE POSITION — migrations 328–330. The live balance, computed in the
// database: opening (entered here when needed, with the date it was true) +
// CALENDAR accrual — 20 annual / 10 personal days per year of service from the
// start date (or the opening's date) — minus approved leave since. The SAME
// view feeds My Leave and Leave approvals, so a rigger, their approver, and
// this file can never show three different numbers.
//
// The editor is director-only, enforced twice: hidden here, and RLS policy
// leave_balances_write on the table refuses anyone else anyway.
// =============================================================================

interface LeavePositionRow {
  employment_type: string;
  accrues:     boolean;
  has_opening: boolean;
  as_at:       string;
  al_opening: number; al_accrued: number; al_taken: number; al_days: number;
  sl_opening: number; sl_accrued: number; sl_taken: number; sl_days: number;
  lsl_weeks:  number;
  rdo_days:   number;
}

function LeavePositionCard({ employeeId, isDirector }: { employeeId: string; isDirector: boolean }) {
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);

  const { data: pos } = useQuery({
    queryKey: ["emp_leave_position_full", employeeId],
    queryFn: async () => {
      // Not in the generated types until the next regen, hence the cast.
      const { data } = await supabase
        .from("v_leave_position" as any)
        .select("*")
        .eq("employee_id", employeeId)
        .maybeSingle();
      return data as unknown as LeavePositionRow | null;
    },
  });

  // Subcontractors / labour hire don't accrue and have no balances to track —
  // showing four zeros on their file would just be noise.
  if (pos && !pos.accrues && !pos.has_opening) return null;

  const num = (n: number | null | undefined) => Number(n ?? 0).toFixed(1);

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <SectionHeading icon={CalendarDays}>Leave</SectionHeading>
          {isDirector && (
            <Button variant="secondary" size="sm" icon={Edit3} onClick={() => setEditOpen(true)}>
              {pos?.has_opening ? "Correct opening balance" : "Set opening balance"}
            </Button>
          )}
        </div>

        {/* Migration 330 — accrual is by the calendar: 20 AL / 10 SL days per
            year of service from the start date (or from a director-entered
            opening's date, when one exists). Timesheets play no part - James,
            26 Aug 2026: "timesheets are too unpredictable while we are so
            small." */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <MiniBalance label="Annual" value={num(pos?.al_days)} unit="days"
            detail={pos ? `${num(pos.al_opening)} + ${num(pos.al_accrued)} − ${num(pos.al_taken)}` : undefined} />
          <MiniBalance label="Personal / sick" value={num(pos?.sl_days)} unit="days"
            detail={pos ? `${num(pos.sl_opening)} + ${num(pos.sl_accrued)} − ${num(pos.sl_taken)}` : undefined} />
          <MiniBalance label="LSL" value={num(pos?.lsl_weeks)} unit="weeks"
            detail="opening − taken" />
          <MiniBalance label="RDO" value={num(pos?.rdo_days)} unit="days"
            detail="opening − taken" />
        </div>

        {pos && (
          <div className="mt-2 text-[11px] text-text-muted font-mono">
            {pos.has_opening
              ? <>Opening as at {fmtDate(pos.as_at)} · accruing since at 20 annual / 10 personal days a year.</>
              : <>Accruing from their start date ({fmtDate(pos.as_at)}) at 20 annual / 10 personal days a year.</>}
          </div>
        )}
      </div>

      {editOpen && (
        <OpeningBalanceEditor
          employeeId={employeeId}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            qc.invalidateQueries({ queryKey: ["emp_leave_position_full", employeeId] });
            qc.invalidateQueries({ queryKey: ["emp_leave_position", employeeId] });
            qc.invalidateQueries({ queryKey: ["my_leave_position"] });
            qc.invalidateQueries({ queryKey: ["leave_approvals_queue"] });
          }}
        />
      )}
    </Card>
  );
}

function MiniBalance({ label, value, unit, detail }: {
  label: string; value: string; unit: string; detail?: string;
}) {
  return (
    <div className="p-3 rounded-md border border-line">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className="text-[18px] font-semibold font-mono mt-1">{value} <span className="text-[11px] font-normal text-text-muted">{unit}</span></div>
      {detail && <div className="text-[10px] text-text-muted font-mono mt-0.5 truncate" title={detail}>{detail}</div>}
    </div>
  );
}

function OpeningBalanceEditor({
  employeeId, onClose, onSaved,
}: {
  employeeId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Pre-fill from the CURRENT ANCHOR row (loaded below), never the computed
  // position — a director correcting the anchor edits what they typed last
  // time, not a figure with accrual already baked in.
  const [al,   setAl]   = useState("");
  const [sl,   setSl]   = useState("");
  const [lsl,  setLsl]  = useState("");
  const [rdo,  setRdo]  = useState("");
  const [asAt, setAsAt] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr]   = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // LSL/RDO openings aren't split out on the view (they're anchor − taken), so
  // read the raw anchor row once for accurate pre-fill of every field.
  useEffect(() => {
    let cancel = false;
    (async () => {
      const { data } = await supabase
        .from("employee_leave_balances")
        .select("annual_leave_days, personal_leave_days, lsl_weeks, rdo_days, last_accrual_date, notes")
        .eq("employee_id", employeeId)
        .maybeSingle();
      if (cancel) return;
      if (data) {
        setAl(String(Number((data as any).annual_leave_days ?? 0)));
        setSl(String(Number((data as any).personal_leave_days ?? 0)));
        setLsl(String(Number((data as any).lsl_weeks ?? 0)));
        setRdo(String(Number((data as any).rdo_days ?? 0)));
        setAsAt((data as any).last_accrual_date ?? "");
        setNotes((data as any).notes ?? "");
      }
      setLoaded(true);
    })();
    return () => { cancel = true; };
  }, [employeeId]);

  async function save() {
    setErr(null);
    if (!asAt) { setErr("The as-at date is required — it's the date these figures were true in Xero. Accrual starts counting from the day after."); return; }
    setBusy(true);
    try {
      const { error } = await supabase
        .from("employee_leave_balances")
        .upsert({
          employee_id:         employeeId,
          annual_leave_days:   al  === "" ? 0 : Number(al),
          personal_leave_days: sl  === "" ? 0 : Number(sl),
          lsl_weeks:           lsl === "" ? 0 : Number(lsl),
          rdo_days:            rdo === "" ? 0 : Number(rdo),
          last_accrual_date:   asAt,
          notes:               notes.trim() || null,
        } as any, { onConflict: "employee_id" });
      if (error) throw error;
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={busy ? undefined : onClose}>
      <div className="w-full max-w-md" onClick={e => e.stopPropagation()}>
        <Card>
          <div className="p-5 border-b border-line">
            <div className="text-[14px] font-medium">Opening leave balance</div>
            <div className="text-[12px] text-text-muted mt-1">
              Only needed as a correction — normally leave just accrues from the
              start date. Enter the true balances and the date they were true;
              from the day after, accrual (20 annual / 10 personal days a year)
              counts from that date instead, minus approved leave.
            </div>
          </div>
          <div className="p-5 space-y-3">
            {!loaded ? (
              <div className="py-4 flex justify-center"><Spinner /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Annual (days)">
                    <Input type="number" step="0.01" mono value={al} onChange={e => setAl(e.target.value)} placeholder="0" />
                  </Field>
                  <Field label="Personal / sick (days)">
                    <Input type="number" step="0.01" mono value={sl} onChange={e => setSl(e.target.value)} placeholder="0" />
                  </Field>
                  <Field label="LSL (weeks)">
                    <Input type="number" step="0.01" mono value={lsl} onChange={e => setLsl(e.target.value)} placeholder="0" />
                  </Field>
                  <Field label="RDO (days)">
                    <Input type="number" step="0.01" mono value={rdo} onChange={e => setRdo(e.target.value)} placeholder="0" />
                  </Field>
                </div>
                <Field label="True as at *" hint="The date these figures were correct. Calendar accrual counts from the day after this.">
                  <Input type="date" mono value={asAt} onChange={e => setAsAt(e.target.value)} />
                </Field>
                <Field label="Notes (optional)">
                  <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. From Xero leave report 30 Jun 26" />
                </Field>
              </>
            )}
            {err && (
              <div className="text-[12px] text-danger border border-danger/30 bg-danger/[0.05] rounded px-3 py-2">
                {err}
              </div>
            )}
          </div>
          <div className="p-4 border-t border-line flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button size="sm" icon={Save} onClick={save} disabled={busy || !loaded}>
              {busy ? <Spinner size={12}/> : "Save balance"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
